import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { MemberService } from '../../../../core/services/member.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { MemberModel } from '../../../../core/models/data/member.model';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { MemberStatuses } from '../../../../core/enums/data.enums';
import { MemberListSortOptions } from '../../../../core/enums/sort-options.enums';

@Component({
	selector: 'ms-dashboard-admin-list',
	imports: [RouterLink, FaDuotoneIconComponent, AvatarComponent],
	templateUrl: './dashboard-admin-list.component.html',
	styleUrl: './dashboard-admin-list.component.scss'
})
export class DashboardAdminListComponent implements OnInit {

	//#region Signals

	public admins = signal<MemberModel[]>([]);
	public isLoading = signal(true);
	public hasError = signal(false);

	//#endregion

	//#region Avatar

	public avatarSize = AvatarConstants.SIZE().MEMBER_CARD;
	public avatarShape = AvatarConstants.SHAPE().MEMBER_CARD;
	public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().MEMBER_CARD;

	//#endregion

	//#region Computed

	public get settingsLink(): string {
		const orgId = this.userSessionService.activeOrganization()?.organizationId;
		return orgId ? `/organizations/profile/${orgId}` : '';
	}

	//#endregion

	private authorizationService = inject(AuthorizationService);
	private memberService = inject(MemberService);
	private userSessionService = inject(UserSessionService);

	public async ngOnInit(): Promise<void> {
		await this.loadAdmins();
	}

	//#region Data Loading

	private async loadAdmins(): Promise<void> {
		this.isLoading.set(true);
		this.hasError.set(false);

		try {
			const rolesResult = await this.authorizationService.getRoles(false);
			if (!rolesResult.success || !rolesResult.data) {
				this.hasError.set(true);
				this.isLoading.set(false);
				return;
			}

			// Role lookup by name is necessary here because we need the roleId to filter members by role via the API.
			// This matches the pattern used in ManageOrganizationAdminsComponent.
			const adminRole = rolesResult.data.find(r => r.name === 'Organization Administrator');
			if (!adminRole) {
				this.hasError.set(true);
				this.isLoading.set(false);
				return;
			}

			const org = this.userSessionService.activeOrganization();
			if (!org) {
				this.isLoading.set(false);
				return;
			}

			const filter: MembersFilter = {
				organizationIds: [org.organizationId],
				roleIds: [adminRole.roleId],
				statuses: [MemberStatuses.Active]
			};

			const result = await this.memberService.searchMembers(
				filter, MemberListSortOptions.NameASC, 0, 20
			);

			if (result.success && result.data?.data) {
				this.admins.set(result.data.data);
			} else {
				this.hasError.set(true);
			}
		} catch (error) {
			this.hasError.set(true);
			console.error('Error loading admins:', error);
		} finally {
			this.isLoading.set(false);
		}
	}

	//#endregion
}
